import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  X,
  Send,
  ExternalLink,
  Minus,
  CalendarPlus,
  Settings,
  Smile,
  Paperclip,
  Mic,
  Square,
  MoreVertical,
  Hand,
  Archive,
  Trash2,
  Pencil,
  Ban,
  UserCircle,
  Check,
  VolumeX,
  Volume2,
  AlertCircle,
  Plus,
  Camera,
  Image as ImageIcon,
  Video,
  Film,
  Phone,
} from 'lucide-react';
import { useMessages } from '../../context/MessagesContext';
import { useAuth } from '../../context/AuthContext';
import useConversationActions from '../../hooks/useConversationActions';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import EmojiPicker from './EmojiPicker';
import PlannerQuickAddModal from './PlannerQuickAddModal';
import AttachmentRenderer from './AttachmentRenderer';
import { useVoiceRecorder } from '../../hooks/useVoiceRecorder';
import useMessageSound from '../../hooks/useMessageSound';
import MessageStatus, { formatBubbleTimestamp } from './MessageStatus';
import ConversationHeaderPresence from './ConversationHeaderPresence';
import { resolveProfileImage } from '../../utils/profileImage';
import CallOverlay from './CallOverlay';
import RecordVideoModal, { isMediaRecorderSupported } from './RecordVideoModal';
import ConnectionProfileDrawer from '../ConnectionProfileDrawer';

const API_URL = window.__BACKEND_URL__;

/**
 * ChatWindow — floating Messenger pop-out.
 *
 * This file deliberately reuses the same backend endpoints and small
 * shared Messenger components as the full /messages page so the pop-out
 * does not become a separate, weaker messaging system.
 *
 * Included parity:
 * - Open full Messenger
 * - View profile hand-off via full Messenger
 * - Add to Planner
 * - Nudge
 * - Mute / Unmute
 * - Archive
 * - Delete chat for me
 * - Block user
 * - Accept / Decline message requests
 * - Attachments/media rendering
 * - Full Messenger-style + media tray
 * - Camera / Photo gallery / Record video / Choose video
 * - Attach file
 * - Emoji picker
 * - Voice notes
 * - Retry failed message
 * - Sent / delivered / read ticks
 * - Per-message Edit / Unsend for everyone / Delete for me
 */

function sortMessages(list) {
  return (Array.isArray(list) ? list : [])
    .filter(Boolean)
    .slice()
    .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
}

function normaliseMessageResponse(data) {
  if (Array.isArray(data)) return sortMessages(data);
  return sortMessages(data?.messages || []);
}

function messageHasText(message) {
  return Boolean((message?.content || '').trim());
}

function getMessageAttachments(message) {
  return Array.isArray(message?.attachments) ? message.attachments.filter(Boolean) : [];
}

function isAdminConversationIdentity(participant) {
  if (participant?.identity_scope === 'member') return false;
  if (participant?.identity_scope === 'admin') return true;
  return participant?.role === 'admin';
}


function getMessageTypeForAttachments(attachments) {
  const first = Array.isArray(attachments) ? attachments[0] : null;
  const type = first?.file_type || first?.kind || first?.type || '';
  if (type === 'image') return 'image';
  if (type === 'audio') return 'voice';
  if (type === 'video') return 'video';
  return 'file';
}

export default function ChatWindow({
  conversationId,
  minimised,
  onMinimise,
  onClose,
  compact = false,
  realtimeEvent = null,
  typingUsers = {},
  wsState = 'closed',
  callsApi = null,
}) {
  const { user } = useAuth();
  const { conversations, markConversationRead, refresh } = useMessages();

  const activeConvo = useMemo(
    () => conversations.find((c) => c.id === conversationId) || null,
    [conversations, conversationId],
  );

  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [draft, setDraft] = useState('');
  const [editingMessage, setEditingMessage] = useState(null);
  const [sending, setSending] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [composerActionsOpen, setComposerActionsOpen] = useState(false);
  const [conversationMenuOpen, setConversationMenuOpen] = useState(false);
  const [recordVideoOpen, setRecordVideoOpen] = useState(false);
  const [profileDrawerUserId, setProfileDrawerUserId] = useState(null);

  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const videoCameraInputRef = useRef(null);
  const videoGalleryInputRef = useRef(null);

  useEffect(() => {
    setEditingMessage(null);
    setDraft('');
    setEmojiOpen(false);
    setComposerActionsOpen(false);
  }, [conversationId]);

  // Messenger-style scroll state for the floating pop-out.
  // The old logic forced the thread to the bottom on every message
  // refresh, which made conversation switches / returning to chat visibly
  // scroll or jump. This tracks the user's real scroll position so we
  // only follow new messages when it feels natural.
  const scrollStateRef = useRef({
    conversationId: null,
    lastMessageId: null,
    messageCount: 0,
    loadingMessages: false,
    wasNearBottom: true,
    initialised: false,
  });

  const recorder = useVoiceRecorder();
  const playSound = useMessageSound();

  const directOther = useMemo(() => {
    if (!activeConvo || activeConvo.is_group || !user?.id) return null;
    return (activeConvo.participants || []).find((p) => p.user_id !== user.id) || null;
  }, [activeConvo, user?.id]);

  const selfParticipant = useMemo(() => {
    if (!activeConvo || !user?.id) return null;
    return (activeConvo.participants || []).find((p) => p.user_id === user.id) || null;
  }, [activeConvo, user?.id]);

  const directOtherIsAdmin = isAdminConversationIdentity(directOther);
  const selfIsAdmin = isAdminConversationIdentity(selfParticipant);

  const recipientIds = useMemo(
    () => (activeConvo?.participants || [])
      .filter((p) => p?.user_id && p.user_id !== user?.id)
      .map((p) => p.user_id),
    [activeConvo?.participants, user?.id],
  );

  const otherName = useMemo(() => {
    if (!activeConvo || !user) return '';
    if (activeConvo.is_group) return activeConvo.title || 'Group chat';

    return directOther?.name || directOther?.email || 'Conversation';
  }, [activeConvo, directOther, user]);

  const otherFirstName = useMemo(() => {
    const name = String(otherName || '').trim();
    if (!name || activeConvo?.is_group) return '';
    return name.split(/\s+/)[0];
  }, [activeConvo?.is_group, otherName]);

  const otherAvatar = useMemo(() => {
    if (!activeConvo || !user) return null;
    if (activeConvo.is_group) return activeConvo.avatar_url || null;

    return resolveProfileImage(directOther);
  }, [activeConvo, directOther, user]);

  const markThreadRead = useCallback(async () => {
    if (!conversationId) return;

    try {
      await axios.post(
        `${API_URL}/api/conversations/${conversationId}/mark-all-read`,
        {},
        { withCredentials: true },
      );
    } catch {
      // Non-fatal. Context mark/read below still keeps the local UI clean.
    }

    try {
      markConversationRead(conversationId);
    } catch {
      // Non-fatal.
    }
  }, [conversationId, markConversationRead]);

  const fetchMessages = useCallback(async ({ showLoader = false } = {}) => {
    if (!conversationId || minimised) return;

    if (showLoader) setLoadingMessages(true);

    try {
      const { data } = await axios.get(
        `${API_URL}/api/conversations/${conversationId}/messages?limit=50`,
        { withCredentials: true },
      );

      setMessages(normaliseMessageResponse(data));
      markThreadRead();
    } catch {
      // Keep pop-out usable even if one poll fails.
    } finally {
      if (showLoader) setLoadingMessages(false);
    }
  }, [conversationId, minimised, markThreadRead]);

  // Fetch + light 8s poll while open.
  useEffect(() => {
    if (!conversationId || minimised) {
      setMessages([]);
      return undefined;
    }

    fetchMessages({ showLoader: true });

    pollRef.current = setInterval(() => {
      fetchMessages({ showLoader: false });
    }, 8000);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [conversationId, minimised, fetchMessages]);

  // Apply the same user-level WebSocket events as the full Messenger.
  // The parent floating dock owns the single socket and fans frames into
  // each open window; each window filters by conversation id.
  useEffect(() => {
    const event = realtimeEvent;
    if (!event) return;

    const eventConversationId = event.conversation_id;
    const belongsHere = eventConversationId === conversationId;

    if (event.type === 'message_unsent') {
      if (!belongsHere) return;
      setMessages((previous) => previous.map((message) => (
        message.id !== event.message_id ? message : {
          ...message,
          deleted: true,
          deleted_reason: 'unsent',
          content: 'This message was unsent',
          attachments: [],
          message_type: 'text',
        }
      )));
      refresh();
      return;
    }

    if (event.type === 'message_edited') {
      if (!belongsHere) return;
      setMessages((previous) => previous.map((message) => (
        message.id !== event.message_id ? message : {
          ...message,
          content: event.content,
          edited_at: event.edited_at,
        }
      )));
      refresh();
      return;
    }

    if (event.type === 'message_admin_deleted') {
      if (!belongsHere) return;
      setMessages((previous) => previous.filter(
        (message) => message.id !== event.message_id,
      ));
      refresh();
      return;
    }

    if (event.type === 'conversation_admin_deleted') {
      if (!belongsHere) return;
      setMessages([]);
      toast.message('This conversation was removed by an administrator.');
      onClose?.();
      refresh();
      return;
    }

    if (event.type === 'message_delivered' || event.type === 'message_read') {
      setMessages((previous) => previous.map((message) => {
        if (message.id !== event.message_id) return message;
        if (event.type === 'message_read' && event.read_by) {
          const readBy = message.read_by || [];
          if (readBy.includes(event.read_by)) return message;
          if (message.sender_id === user?.id) playSound('read');
          return { ...message, read_by: [...readBy, event.read_by] };
        }
        if (event.type === 'message_delivered' && Array.isArray(event.delivered_to)) {
          return {
            ...message,
            delivered_to: Array.from(new Set([
              ...(message.delivered_to || []),
              ...event.delivered_to,
            ])),
          };
        }
        return message;
      }));
      return;
    }

    if (!belongsHere || !event.id) return;

    setMessages((previous) => {
      if (previous.some((message) => message.id === event.id)) return previous;
      if (event.sender_id && event.sender_id !== user?.id) {
        playSound('received');
        axios.post(
          `${API_URL}/api/messages/${event.id}/delivered`,
          {},
          { withCredentials: true },
        ).catch(() => {});
      }
      return [...previous, event];
    });
    markThreadRead();
    refresh();
  }, [
    conversationId,
    markThreadRead,
    onClose,
    playSound,
    realtimeEvent,
    refresh,
    user?.id,
  ]);

    const isThreadNearBottom = useCallback((threshold = 96) => {
    const el = scrollRef.current;
    if (!el) return true;

    return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
  }, []);

  const scrollThreadToLatest = useCallback(({ smooth = false } = {}) => {
    const el = scrollRef.current;
    if (!el) return;

    const run = () => {
      const node = scrollRef.current;
      if (!node) return;

      if (smooth && typeof node.scrollTo === 'function') {
        node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
      } else {
        node.scrollTop = node.scrollHeight;
      }
    };

    // Media previews and fonts can expand after the first layout pass.
    // Settle repeatedly only for an initial instant jump; smooth new-
    // message scrolling remains a single animation and never fights a
    // user who is reading older messages.
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(run);
      if (!smooth) {
        window.setTimeout(run, 80);
        window.setTimeout(run, 250);
        window.setTimeout(run, 600);
      }
    } else {
      run();
    }
  }, []);

  // Messenger-style auto-scroll:
  // - conversation switch / return / first load: jump instantly to latest
  //   with no visible animation.
  // - own new message: follow smoothly.
  // - incoming message: follow only if the user was already near bottom.
  // - user reading older messages: preserve their scroll position.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const lastMessage = messages[messages.length - 1] || null;
    const lastMessageId = lastMessage?.id || null;
    const previous = scrollStateRef.current;

    const conversationChanged = previous.conversationId !== conversationId;
    const finishedInitialLoad =
      previous.loadingMessages === true
      && loadingMessages === false
      && previous.messageCount === 0
      && messages.length > 0;

    const messageCountIncreased = messages.length > previous.messageCount;
    const lastMessageChanged = Boolean(lastMessageId && lastMessageId !== previous.lastMessageId);
    const latestIsMine = Boolean(lastMessage?.sender_id && lastMessage.sender_id === user?.id);

    if (el) {
      if (conversationChanged || finishedInitialLoad) {
        scrollThreadToLatest({ smooth: false });
      } else if (messageCountIncreased && lastMessageChanged && (latestIsMine || previous.wasNearBottom)) {
        scrollThreadToLatest({ smooth: true });
      }
    }

    scrollStateRef.current = {
      conversationId,
      lastMessageId,
      messageCount: messages.length,
      loadingMessages,
      wasNearBottom: el ? isThreadNearBottom() : previous.wasNearBottom,
      initialised: true,
    };
  }, [
    conversationId,
    isThreadNearBottom,
    loadingMessages,
    messages,
    scrollThreadToLatest,
    user?.id,
  ]);

  const reloadAfterAction = useCallback(async () => {
    await fetchMessages({ showLoader: false });
    refresh?.();
  }, [fetchMessages, refresh]);

  // Shared conversation-level actions (nudge, archive, mute, delete, block,
  // accept/decline, etc.). Confirmation dialogs stay in this component —
  // the hook only owns the API call, busy state and toast feedback.
  const {
    availableActions,
    nudge: nudgeAction,
    archive: archiveAction,
    deleteForMe: deleteForMeAction,
    block: blockAction,
    toggleMute: toggleMuteAction,
    accept: acceptAction,
    decline: declineAction,
  } = useConversationActions(activeConvo, {
    currentUserId: user?.id,
    onAfterMutate: reloadAfterAction,
    onConversationGone: () => {
      refresh?.();
      onClose?.();
    },
  });

  const closeAllLocalPopovers = useCallback(() => {
    setEmojiOpen(false);
    setComposerActionsOpen(false);
  }, []);

  const sendWithAttachments = async ({
    text = '',
    attachments = [],
    message_type = 'text',
    retryingMessageId = null,
  }) => {
    if (!conversationId || !user?.id) return;

    const content = (text || '').trim();
    const cleanAttachments = Array.isArray(attachments) ? attachments.filter(Boolean) : [];
    const isEditingTextMessage = Boolean(
      editingMessage?.id
      && !retryingMessageId
      && cleanAttachments.length === 0
      && message_type === 'text',
    );

    if (isEditingTextMessage) {
      if (!content) {
        toast.error('Message cannot be empty');
        return;
      }

      if (content === (editingMessage.content || '').trim()) {
        setEditingMessage(null);
        setDraft('');
        return;
      }

      setSending(true);
      closeAllLocalPopovers();

      try {
        const { data } = await axios.patch(
          `${API_URL}/api/messages/${editingMessage.id}`,
          { content },
          { withCredentials: true },
        );

        setMessages((prev) =>
          prev.map((m) =>
            m.id === editingMessage.id
              ? {
                  ...m,
                  content: data.content || content,
                  edited_at: data.edited_at || new Date().toISOString(),
                }
              : m,
          ),
        );

        setEditingMessage(null);
        setDraft('');
        toast.success('Message updated');
        refresh?.();
      } catch (err) {
        const status = err?.response?.status;

        if (status === 409) {
          toast.error("This message has already been read, so it can’t be edited or unsent.");
        } else if (status === 403) {
          toast.error('You can only edit your own messages.');
        } else {
          toast.error(err?.response?.data?.detail || 'Could not edit message');
        }
      } finally {
        setSending(false);
      }

      return;
    }

    if (!content && cleanAttachments.length === 0) return;

    setSending(true);
    closeAllLocalPopovers();

    const tempId = retryingMessageId || `temp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      conversation_id: conversationId,
      sender_id: user.id,
      sender_name: selfIsAdmin
        ? (selfParticipant?.name || 'Admin Support')
        : user.name,
      sender_role: selfIsAdmin ? 'admin' : user.role,
      ...(selfIsAdmin
        ? {
            identity_scope: 'admin',
            sender_position: selfParticipant?.position || 'CCUK Support',
          }
        : {}),
      content,
      message_type,
      attachments: cleanAttachments,
      read_by: [user.id],
      delivered_to: [],
      created_at: new Date().toISOString(),
      _pending: true,
    };

    setMessages((prev) => {
      const withoutRetry = retryingMessageId
        ? prev.filter((m) => m.id !== retryingMessageId)
        : prev;

      return [...withoutRetry, optimistic];
    });

    setDraft('');

    try {
      const { data } = await axios.post(
        `${API_URL}/api/messages`,
        {
          conversation_id: conversationId,
          content,
          message_type,
          attachments: cleanAttachments,
        },
        { withCredentials: true },
      );

      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempId
            ? {
                ...m,
                id: data.message_id || data.id || tempId,
                _pending: false,
                _failed: false,
              }
            : m,
        ),
      );

      playSound('sent');
      await reloadAfterAction();
    } catch (err) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempId
            ? {
                ...m,
                _pending: false,
                _failed: true,
                _error: err?.response?.data?.detail || 'Failed to send',
              }
            : m,
        ),
      );

      toast.error(err?.response?.data?.detail || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  const send = () => sendWithAttachments({ text: draft });

  const handleRetryFailedMessage = async (failed) => {
    if (!failed) return;

    await sendWithAttachments({
      text: failed.content || '',
      attachments: getMessageAttachments(failed),
      message_type: failed.message_type || getMessageTypeForAttachments(getMessageAttachments(failed)),
      retryingMessageId: failed.id,
    });
  };

  // Reuses the SAME backend uploader as the main Messenger:
  // POST /api/messages/upload (FormData, file). Same security / mime / size
  // rules apply because we hit the same endpoint.
  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append('file', file);

    const { data } = await axios.post(`${API_URL}/api/messages/upload`, fd, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return data;
  };

  const onPickFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow picking the same file again
    if (!files.length) return;

    closeAllLocalPopovers();
    setUploading(true);

    try {
      const uploaded = [];

      for (const f of files) {
        try {
          const d = await uploadOne(f);
          uploaded.push(d);
        } catch (err) {
          toast.error(err?.response?.data?.detail || `Could not upload ${f.name}`);
        }
      }

      if (uploaded.length) {
        await sendWithAttachments({
          text: '',
          attachments: uploaded,
          message_type: getMessageTypeForAttachments(uploaded),
        });
      }
    } finally {
      setUploading(false);
    }
  };

  const handleVoiceClick = async () => {
    closeAllLocalPopovers();

    if (recorder.recording) {
      const result = await recorder.stop();
      if (!result?.blob) return;

      setUploading(true);

      try {
        const f = new File([result.blob], `voice_${Date.now()}.webm`, {
          type: result.mimeType || 'audio/webm',
        });

        const d = await uploadOne(f);
        d.file_type = d.file_type || 'audio';
        d.duration_sec = result.duration;

        await sendWithAttachments({
          text: '',
          attachments: [d],
          message_type: 'voice',
        });
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not send voice note');
      } finally {
        setUploading(false);
      }
    } else {
      try {
        await recorder.start();
      } catch {
        toast.error('Microphone unavailable');
      }
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const handleEditMessage = (message) => {
    if (!message?.id) return;

    setEditingMessage(message);
    setDraft(message.content || '');
    closeAllLocalPopovers();

    setTimeout(() => {
      textareaRef.current?.focus();
    }, 30);
  };

  const cancelEditMessage = () => {
    setEditingMessage(null);
    setDraft('');
    closeAllLocalPopovers();

    setTimeout(() => {
      textareaRef.current?.focus();
    }, 30);
  };

  const handleUnsendMessage = async (messageId) => {
    if (!messageId) return;

    try {
      await axios.delete(`${API_URL}/api/messages/${messageId}`, { withCredentials: true });

      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? {
                ...m,
                deleted: true,
                deleted_reason: 'unsent',
                content: 'This message was unsent',
                attachments: [],
                message_type: 'text',
              }
            : m,
        ),
      );

      if (editingMessage?.id === messageId) {
        setEditingMessage(null);
        setDraft('');
      }

      toast.success('Message unsent for everyone');
      refresh?.();
    } catch (err) {
      const status = err?.response?.status;

      if (status === 409) {
        toast.error("This message has already been read, so it can’t be edited or unsent.");
      } else if (status === 403) {
        toast.error('You can only unsend your own messages.');
      } else {
        toast.error(err?.response?.data?.detail || 'Could not unsend message');
      }
    }
  };

  const handleDeleteMessageForMe = async (messageId) => {
    if (!messageId) return;

    try {
      await axios.delete(`${API_URL}/api/messages/${messageId}/for-me`, { withCredentials: true });

      setMessages((prev) => prev.filter((m) => m.id !== messageId));

      if (editingMessage?.id === messageId) {
        setEditingMessage(null);
        setDraft('');
      }

      toast.success('Deleted for you');
      refresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete message for you');
    }
  };

  // ── Conversation actions ─────────────────────────────────────────────
  // The actual API calls live in `useConversationActions`. These thin
  // wrappers exist so the JSX (which referenced these names) does not
  // need to change, and so future confirmation dialogs can be slotted
  // in here without touching the menu markup.
  const handleNudge = () => nudgeAction();
  const handleArchive = () => archiveAction();
  const handleDeleteForMe = () => deleteForMeAction();
  const handleBlock = () => blockAction();
  const handleAcceptRequest = () => acceptAction();
  const handleDeclineRequest = () => declineAction();
  const handleToggleMute = () => toggleMuteAction();

  const openFullMessenger = () => {
    window.location.href = `/messages/${conversationId}`;
  };

  const openMessagingSettings = () => {
    window.location.href = '/dashboard?tab=settings&section=messaging';
  };

  const handleViewProfile = () => {
    if (directOtherIsAdmin) return;
    if (directOther?.user_id) setProfileDrawerUserId(directOther.user_id);
  };

  if (!activeConvo) return null;

  // Minimised pill for this window (used in desktop stack).
  if (minimised && !compact) {
    return (
      <button
        onClick={() => onMinimise?.(false)}
        className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-[#002FA7] hover:bg-[#0055FF] text-white shadow-lg transition-colors"
        data-testid={`chat-window-pill-${conversationId}`}
      >
        <span className="h-2 w-2 rounded-full bg-white/70" />
        <span className="text-xs font-medium truncate max-w-[140px]">{otherName}</span>
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onClose?.();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              onClose?.();
            }
          }}
          className="opacity-60 hover:opacity-100 cursor-pointer"
          data-testid={`chat-window-pill-close-${conversationId}`}
        >
          <X className="h-3.5 w-3.5" />
        </span>
      </button>
    );
  }

  const widthClass = compact ? 'w-full' : 'w-[380px]';
  const heightClass = compact ? 'h-full' : 'h-[560px] max-h-[calc(100dvh-6rem)]';
  const roundingClass = compact ? '' : 'rounded-t-2xl';
  const blockLabel = otherFirstName ? `Block ${otherFirstName}` : 'Block user';

  return (
    <div
      className={`${widthClass} ${heightClass} ${roundingClass} bg-theme-surface border border-theme shadow-2xl flex flex-col overflow-hidden`}
      data-testid={`chat-window-${conversationId}`}
      role="dialog"
      aria-label={`Chat with ${otherName}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-theme-secondary border-b border-theme shrink-0">
        {otherAvatar ? (
          <img src={otherAvatar} alt="" className="h-8 w-8 rounded-full object-cover" />
        ) : (
          <div className="h-8 w-8 rounded-full bg-[#002FA7] text-white flex items-center justify-center text-xs font-semibold">
            {(otherName || '?').charAt(0).toUpperCase()}
          </div>
        )}

        <button
          type="button"
          onClick={openFullMessenger}
          className="flex-1 min-w-0 text-left"
          title="Open full Messenger"
          data-testid={`chat-window-title-${conversationId}`}
        >
          <div className="text-xs font-semibold text-theme-text truncate">{otherName}</div>
          <div className="text-[9px] text-theme-muted uppercase tracking-wider truncate">
            {activeConvo?.is_group ? (
              `Group · ${(activeConvo.participants || []).length} members`
            ) : directOtherIsAdmin ? (
              <span className="normal-case">
                {directOther?.position || 'CCUK Support'} · CCUK Admin
              </span>
            ) : activeConvo?.muted ? (
              'Muted conversation'
            ) : directOther?.user_id && typingUsers?.[directOther.user_id] ? (
              <span className="text-[#002FA7] normal-case">Typing…</span>
            ) : directOther?.user_id ? (
              // Live presence + professional status + contact availability.
              <ConversationHeaderPresence peerId={directOther.user_id} fallback={<span>Messenger</span>} />
            ) : (
              'Messenger'
            )}
          </div>
        </button>

        <button
          type="button"
          onClick={openFullMessenger}
          className="text-theme-muted hover:text-[#002FA7] p-1 rounded"
          title="Open in full Messenger"
          data-testid={`chat-window-open-full-${conversationId}`}
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </button>

        <button
          type="button"
          onClick={() => setPlannerOpen(true)}
          className="text-theme-muted hover:text-[#002FA7] p-1 rounded"
          title="Add to Planner"
          data-testid={`chat-window-planner-${conversationId}`}
        >
          <CalendarPlus className="h-3.5 w-3.5" />
        </button>

        {callsApi && !activeConvo?.is_group && directOther?.user_id && (
          <>
            <button
              type="button"
              onClick={() => callsApi.placeCall(
                conversationId,
                directOther.user_id,
                'voice',
                { peerName: otherName, peerAvatar: otherAvatar },
              )}
              disabled={wsState !== 'open' || callsApi.mediaInProgress || !!callsApi.call}
              className="text-theme-muted hover:text-[#002FA7] p-1 rounded disabled:opacity-40"
              title={wsState === 'open' ? 'Voice call' : 'Connecting…'}
              aria-label="Start voice call"
              data-testid={`chat-window-voice-call-${conversationId}`}
            >
              <Phone className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => callsApi.placeCall(
                conversationId,
                directOther.user_id,
                'video',
                { peerName: otherName, peerAvatar: otherAvatar },
              )}
              disabled={wsState !== 'open' || callsApi.mediaInProgress || !!callsApi.call}
              className="text-theme-muted hover:text-[#002FA7] p-1 rounded disabled:opacity-40"
              title={wsState === 'open' ? 'Video call' : 'Connecting…'}
              aria-label="Start video call"
              data-testid={`chat-window-video-call-${conversationId}`}
            >
              <Video className="h-3.5 w-3.5" />
            </button>
          </>
        )}

        <DropdownMenu
          modal={false}
          open={conversationMenuOpen}
          onOpenChange={(next) => {
            setConversationMenuOpen(next);
            if (next) closeAllLocalPopovers();
          }}
        >
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              onPointerDown={(e) => {
                // Radix listens on pointer events. Stop this reaching any
                // parent pop-out handlers so the menu does not instantly close.
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="text-theme-muted hover:text-theme-text p-1 rounded"
              title="More actions"
              data-testid={`chat-window-more-${conversationId}`}
              aria-label="More conversation actions"
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="end"
            sideOffset={8}
            onCloseAutoFocus={(event) => event.preventDefault()}
            onPointerDownOutside={(event) => {
              // Keep Radix happy inside the floating dock; outside click
              // should close the menu, but it should not close the chat.
              event.stopPropagation();
            }}
            className="w-56 z-[1000] bg-theme-surface border-theme shadow-2xl pointer-events-auto"
            data-testid={`chat-window-more-menu-${conversationId}`}
          >
            <DropdownMenuItem
              onClick={() => {
                setConversationMenuOpen(false);
                openFullMessenger();
              }}
              data-testid={`chat-window-open-full-menu-${conversationId}`}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open full Messenger
            </DropdownMenuItem>

            {!activeConvo.is_group && !directOtherIsAdmin && (
              <DropdownMenuItem
                onClick={() => {
                  setConversationMenuOpen(false);
                  handleViewProfile();
                }}
                data-testid={`chat-window-view-profile-${conversationId}`}
              >
                <UserCircle className="h-4 w-4 mr-2" />
                View profile
              </DropdownMenuItem>
            )}

            <DropdownMenuItem
              onClick={() => {
                setConversationMenuOpen(false);
                setPlannerOpen(true);
              }}
              data-testid={`chat-window-planner-menu-${conversationId}`}
            >
              <CalendarPlus className="h-4 w-4 mr-2" />
              Add to Planner
            </DropdownMenuItem>

            {availableActions.canNudge && (
              <DropdownMenuItem
                onClick={() => {
                  setConversationMenuOpen(false);
                  handleNudge();
                }}
                data-testid={`chat-window-nudge-${conversationId}`}
              >
                <Hand className="h-4 w-4 mr-2" />
                Nudge
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={() => {
                setConversationMenuOpen(false);
                handleToggleMute();
              }}
              data-testid={`chat-window-mute-${conversationId}`}
            >
              {availableActions.canUnmute ? (
                <>
                  <Volume2 className="h-4 w-4 mr-2" />
                  Unmute conversation
                </>
              ) : (
                <>
                  <VolumeX className="h-4 w-4 mr-2" />
                  Mute conversation
                </>
              )}
            </DropdownMenuItem>

            {availableActions.canArchive && (
              <DropdownMenuItem
                onClick={() => {
                  setConversationMenuOpen(false);
                  handleArchive();
                }}
                data-testid={`chat-window-archive-${conversationId}`}
              >
                <Archive className="h-4 w-4 mr-2" />
                Archive chat
              </DropdownMenuItem>
            )}

            {availableActions.canDeleteForMe && (
              <DropdownMenuItem
                onClick={() => {
                  setConversationMenuOpen(false);
                  handleDeleteForMe();
                }}
                className="text-rose-500 focus:text-rose-500"
                data-testid={`chat-window-delete-${conversationId}`}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete chat for me
              </DropdownMenuItem>
            )}

            {availableActions.canBlock && (
              <DropdownMenuItem
                onClick={() => {
                  setConversationMenuOpen(false);
                  handleBlock();
                }}
                className="text-rose-500 focus:text-rose-500"
                data-testid={`chat-window-block-${conversationId}`}
              >
                <Ban className="h-4 w-4 mr-2" />
                {blockLabel}
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={() => {
                setConversationMenuOpen(false);
                openMessagingSettings();
              }}
              data-testid={`chat-window-messaging-settings-${conversationId}`}
            >
              <Settings className="h-4 w-4 mr-2" />
              Messaging settings
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {!compact && onMinimise && (
          <button
            type="button"
            onClick={() => onMinimise(true)}
            className="text-theme-muted hover:text-theme-text p-1 rounded"
            title="Minimise"
            data-testid={`chat-window-minimise-${conversationId}`}
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
        )}

        <button
          type="button"
          onClick={onClose}
          className="text-theme-muted hover:text-theme-text p-1 rounded"
          title="Close"
          data-testid={`chat-window-close-${conversationId}`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {wsState === 'reconnecting' && (
        <div className="shrink-0 bg-amber-500/10 text-amber-600 text-[10px] text-center py-1 border-b border-amber-500/20">
          Reconnecting…
        </div>
      )}

      {/* Messages */}
      <div
        ref={scrollRef}
        onScroll={() => {
          scrollStateRef.current.wasNearBottom = isThreadNearBottom();
        }}
        className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-2 space-y-1.5 bg-theme [overscroll-behavior:contain]"
        data-testid={`chat-window-messages-${conversationId}`}
      >
        {loadingMessages && messages.length === 0 ? (
          <div className="text-center text-[11px] text-theme-muted py-4">Loading…</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-[11px] text-theme-muted py-4">No messages yet. Say hi.</div>
        ) : (
          messages.map((m) => {
            if (!m?.id) return null;

            const mine = m.sender_id === user?.id;
            const isSystem = m.message_type === 'system';
            const isNudge = m.message_type === 'nudge';
            const isDeleted = !!m.deleted;
            const attachments = getMessageAttachments(m);
            const hasText = messageHasText(m) || isDeleted;
            const isVoice = m.message_type === 'voice';
            const readByOthers = (Array.isArray(m.read_by) ? m.read_by : []).filter((id) => id !== user?.id);

            const canUnsend = mine
              && !isDeleted
              && !isSystem
              && !isNudge
              && !m._pending
              && !m._failed
              && readByOthers.length === 0;

            const canEdit = canUnsend && messageHasText(m) && !isVoice;

            const canDeleteMessageForMe = mine
              && !isDeleted
              && !isSystem
              && !isNudge
              && !m._pending;

            if (isSystem) {
              return (
                <div key={m.id} className="flex justify-center py-1">
                  <span className="text-[10px] text-theme-muted italic text-center max-w-[90%]">
                    {m.content || 'System update'}
                  </span>
                </div>
              );
            }

            if (isNudge) {
              return (
                <div key={m.id} className="flex justify-center py-1">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-300 border border-amber-500/25 px-2.5 py-1 text-[10px] font-medium">
                    <Hand className="h-3 w-3" />
                    {mine ? 'You sent a nudge' : `${m.sender_name || 'They'} nudged you`}
                  </span>
                </div>
              );
            }

            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[82%] flex flex-col gap-0.5 ${mine ? 'items-end' : 'items-start'}`}>
                  {activeConvo?.is_group && !mine && (
                    <div className="text-[9px] font-semibold text-theme-muted px-1">
                      {m.sender_name || 'User'}
                    </div>
                  )}

                  {hasText && (
                    <div
                      className={`px-2.5 py-1.5 rounded-xl text-xs whitespace-pre-wrap break-words [overflow-wrap:anywhere] ${
                        mine
                          ? 'bg-[#002FA7] text-white rounded-br-sm'
                          : 'bg-theme-surface border border-theme text-theme-text rounded-bl-sm'
                      }`}
                    >
                      {isDeleted ? (
                        <span className="italic opacity-70">This message was unsent</span>
                      ) : (
                        m.content
                      )}
                    </div>
                  )}

                  {!isDeleted && attachments.length > 0 && (
                    <div className={`flex flex-wrap gap-1.5 mt-0.5 ${mine ? 'justify-end' : 'justify-start'}`}>
                      {attachments.map((attachment, index) => (
                        <AttachmentRenderer
                          key={`${m.id}-attachment-${index}`}
                          attachment={attachment}
                          isMine={mine}
                          onOpenImage={setLightboxUrl}
                        />
                      ))}
                    </div>
                  )}

                  <div className={`flex items-center gap-1 px-1 text-[9.5px] text-theme-muted ${mine ? 'flex-row-reverse' : ''}`}>
                    <span>{formatBubbleTimestamp(m.created_at)}</span>

                    {m.edited_at && !isDeleted && (
                      <span className="italic">· edited</span>
                    )}

                    {mine && m._failed && (
                      <button
                        type="button"
                        onClick={() => handleRetryFailedMessage(m)}
                        className="inline-flex items-center gap-0.5 rounded-full px-1 py-0.5 text-rose-500 hover:bg-rose-500/10 font-medium"
                        title={m._error || 'Retry failed message'}
                        data-testid={`chat-window-retry-${m.id}`}
                      >
                        <AlertCircle className="h-3 w-3" />
                        Retry
                      </button>
                    )}

                    {mine && !isDeleted && !m._failed && (
                      <MessageStatus
                        message={m}
                        selfId={user.id}
                        recipientIds={recipientIds}
                        readReceiptsEnabled
                        size="sm"
                      />
                    )}

                    {mine && !isDeleted && (canEdit || canUnsend || canDeleteMessageForMe) && (
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="h-5 w-5 rounded-full inline-flex items-center justify-center text-theme-muted hover:text-theme-text hover:bg-theme-surface transition-colors"
                            title="Message actions"
                            data-testid={`chat-window-message-menu-${m.id}`}
                            aria-label="Message actions"
                          >
                            <MoreVertical className="h-3 w-3" />
                          </button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent
                          align={mine ? 'end' : 'start'}
                          sideOffset={6}
                          onCloseAutoFocus={(event) => event.preventDefault()}
                          className="w-48 z-[1000] bg-theme-surface border-theme shadow-2xl"
                        >
                          {canEdit && (
                            <DropdownMenuItem
                              onClick={() => handleEditMessage(m)}
                              className="text-theme-text cursor-pointer"
                              data-testid={`chat-window-edit-message-${m.id}`}
                            >
                              <Pencil className="h-3.5 w-3.5 mr-2" />
                              Edit message
                            </DropdownMenuItem>
                          )}

                          {canUnsend && (
                            <DropdownMenuItem
                              onClick={() => handleUnsendMessage(m.id)}
                              className="text-rose-500 focus:text-rose-600 cursor-pointer"
                              data-testid={`chat-window-unsend-message-${m.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5 mr-2" />
                              Unsend for everyone
                            </DropdownMenuItem>
                          )}

                          {canDeleteMessageForMe && (
                            <DropdownMenuItem
                              onClick={() => handleDeleteMessageForMe(m.id)}
                              className="text-theme-text cursor-pointer"
                              data-testid={`chat-window-delete-message-for-me-${m.id}`}
                            >
                              <X className="h-3.5 w-3.5 mr-2" />
                              Delete for me
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Composer — adapts to pending/blocked state, mirroring the full page UX */}
      {activeConvo.status === 'pending'
        && activeConvo.initiated_by === user?.id
        && !(selfIsAdmin && messages.length === 0) ? (
        <div
          className="border-t border-theme bg-theme-surface text-center px-3 py-3 text-[11px] text-theme-muted shrink-0 pb-[calc(0.75rem+env(safe-area-inset-bottom))]"
          data-testid={`chat-window-pending-sender-${conversationId}`}
        >
          Message request pending — they need to accept before you can send more.
        </div>
      ) : activeConvo.status === 'pending'
        && activeConvo.initiated_by !== user?.id ? (
        <div
          className="border-t border-theme bg-theme-surface px-3 py-2.5 flex items-center gap-2 shrink-0 pb-[calc(0.625rem+env(safe-area-inset-bottom))]"
          data-testid={`chat-window-pending-recipient-${conversationId}`}
        >
          <span className="text-[11px] text-theme-muted flex-1 min-w-0 truncate">
            {otherName} wants to chat
          </span>

          <Button
            size="sm"
            onClick={handleDeclineRequest}
            variant="outline"
            className="h-7 text-[11px] px-2 border-theme text-theme-text"
            data-testid={`chat-window-decline-${conversationId}`}
          >
            <X className="h-3 w-3 mr-1" />
            Decline
          </Button>

          <Button
            size="sm"
            onClick={handleAcceptRequest}
            className="h-7 text-[11px] px-2 bg-[#002FA7] hover:bg-[#0055FF] text-white"
            data-testid={`chat-window-accept-${conversationId}`}
          >
            <Check className="h-3 w-3 mr-1" />
            Accept
          </Button>
        </div>
      ) : activeConvo.status === 'blocked' ? (
        <div className="border-t border-theme bg-theme-surface text-center px-3 py-3 text-[11px] text-rose-500 shrink-0 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
          This conversation has been blocked.
        </div>
      ) : (
        <div className="relative p-2 border-t border-theme bg-theme-surface shrink-0 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
          {emojiOpen && (
            <div className="absolute bottom-[58px] left-2 right-2 z-20">
              <EmojiPicker
                onSelect={(e) => {
                  setDraft((d) => d + e);
                  setTimeout(() => textareaRef.current?.focus(), 30);
                }}
                onClose={() => setEmojiOpen(false)}
              />
            </div>
          )}

          {editingMessage && !recorder.recording && (
            <div
              className="mb-2 flex items-center justify-between gap-2 rounded-xl bg-[#002FA7]/5 border border-[#002FA7]/20 px-2.5 py-2"
              data-testid={`chat-window-editing-message-${conversationId}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Pencil className="h-3.5 w-3.5 text-[#002FA7] shrink-0" />
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-theme-text leading-tight">Editing message</p>
                  <p className="text-[10px] text-theme-muted truncate max-w-[220px]">
                    {editingMessage.content}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={cancelEditMessage}
                className="shrink-0 text-[11px] font-medium text-theme-muted hover:text-theme-text px-2 py-1 rounded-lg hover:bg-theme-hover"
                data-testid={`chat-window-cancel-edit-message-${conversationId}`}
              >
                Cancel
              </button>
            </div>
          )}

          {composerActionsOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setComposerActionsOpen(false)}
                aria-hidden="true"
                data-testid={`chat-window-actions-backdrop-${conversationId}`}
              />
              <div
                className="absolute bottom-[58px] left-2 z-30 w-[min(270px,calc(100dvw-2rem))] rounded-2xl bg-theme-surface border border-theme/60 p-1.5 shadow-2xl"
                data-testid={`chat-window-actions-popover-${conversationId}`}
                role="menu"
              >
                <button
                  type="button"
                  onClick={() => {
                    setComposerActionsOpen(false);
                    cameraInputRef.current?.click();
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-xl hover:bg-[#002FA7]/10 active:bg-[#002FA7]/20 transition-colors"
                  data-testid={`chat-window-camera-${conversationId}`}
                >
                  <span className="h-9 w-9 shrink-0 rounded-full bg-[#002FA7]/10 flex items-center justify-center text-[#002FA7]">
                    <Camera className="h-[18px] w-[18px]" />
                  </span>
                  <span className="text-sm font-medium text-theme-text">Camera</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setComposerActionsOpen(false);
                    galleryInputRef.current?.click();
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-xl hover:bg-[#002FA7]/10 active:bg-[#002FA7]/20 transition-colors"
                  data-testid={`chat-window-gallery-${conversationId}`}
                >
                  <span className="h-9 w-9 shrink-0 rounded-full bg-[#002FA7]/10 flex items-center justify-center text-[#002FA7]">
                    <ImageIcon className="h-[18px] w-[18px]" />
                  </span>
                  <span className="text-sm font-medium text-theme-text">Photo gallery</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setComposerActionsOpen(false);
                    if (isMediaRecorderSupported()) {
                      setRecordVideoOpen(true);
                    } else {
                      videoCameraInputRef.current?.click();
                    }
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-xl hover:bg-[#002FA7]/10 active:bg-[#002FA7]/20 transition-colors"
                  data-testid={`chat-window-record-video-${conversationId}`}
                >
                  <span className="h-9 w-9 shrink-0 rounded-full bg-[#002FA7]/10 flex items-center justify-center text-[#002FA7]">
                    <Video className="h-[18px] w-[18px]" />
                  </span>
                  <span className="text-sm font-medium text-theme-text">Record video</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setComposerActionsOpen(false);
                    videoGalleryInputRef.current?.click();
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-xl hover:bg-[#002FA7]/10 active:bg-[#002FA7]/20 transition-colors"
                  data-testid={`chat-window-choose-video-${conversationId}`}
                >
                  <span className="h-9 w-9 shrink-0 rounded-full bg-[#002FA7]/10 flex items-center justify-center text-[#002FA7]">
                    <Film className="h-[18px] w-[18px]" />
                  </span>
                  <span className="text-sm font-medium text-theme-text">Choose video</span>
                </button>
              </div>
            </>
          )}

          {recorder.recording && (
            <div className="absolute -top-7 left-2 right-2 flex items-center justify-between bg-rose-500/95 text-white text-[11px] rounded-md px-2 py-1 shadow-lg">
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                Recording…
              </span>
              <span className="tabular-nums">{recorder.elapsed}s</span>
            </div>
          )}

          <div className="flex items-end gap-1">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.mp4,.webm,.mov"
              onChange={onPickFile}
              data-testid={`chat-window-file-input-${conversationId}`}
            />

            <input
              ref={cameraInputRef}
              type="file"
              className="hidden"
              accept="image/*"
              capture="environment"
              onChange={onPickFile}
              data-testid={`chat-window-camera-input-${conversationId}`}
            />

            <input
              ref={galleryInputRef}
              type="file"
              multiple
              className="hidden"
              accept="image/*"
              onChange={onPickFile}
              data-testid={`chat-window-gallery-input-${conversationId}`}
            />

            <input
              ref={videoCameraInputRef}
              type="file"
              className="hidden"
              accept="video/*"
              capture="environment"
              onChange={onPickFile}
              data-testid={`chat-window-video-camera-input-${conversationId}`}
            />

            <input
              ref={videoGalleryInputRef}
              type="file"
              multiple
              className="hidden"
              accept="video/*"
              onChange={onPickFile}
              data-testid={`chat-window-video-gallery-input-${conversationId}`}
            />

            <button
              type="button"
              onClick={() => {
                setEmojiOpen(false);
                setComposerActionsOpen((open) => !open);
              }}
              disabled={uploading || sending || recorder.recording || !!editingMessage}
              className="h-8 w-8 rounded-full flex items-center justify-center text-[#002FA7] bg-[#002FA7]/10 hover:bg-[#002FA7]/15 active:scale-95 transition-all shrink-0 disabled:opacity-40"
              title="More actions"
              data-testid={`chat-window-plus-${conversationId}`}
              aria-label="More message actions"
              aria-expanded={composerActionsOpen}
            >
              {composerActionsOpen ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            </button>

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || sending || recorder.recording || !!editingMessage}
              className="h-8 w-8 rounded-full flex items-center justify-center text-[#002FA7] hover:bg-[#002FA7]/10 active:bg-[#002FA7]/20 shrink-0 disabled:opacity-40"
              title="Attach file"
              data-testid={`chat-window-attach-${conversationId}`}
            >
              <Paperclip className="h-4 w-4" />
            </button>

            <button
              type="button"
              onClick={handleVoiceClick}
              disabled={uploading || sending || !recorder.supported || !!editingMessage}
              className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 disabled:opacity-40 ${
                recorder.recording
                  ? 'text-white bg-rose-500 hover:bg-rose-600'
                  : 'text-[#002FA7] hover:bg-[#002FA7]/10 active:bg-[#002FA7]/20'
              }`}
              title={
                recorder.recording
                  ? 'Stop & send voice note'
                  : recorder.supported
                    ? 'Record voice note'
                    : 'Voice notes unavailable in this browser'
              }
              data-testid={`chat-window-voice-${conversationId}`}
            >
              {recorder.recording ? (
                <Square className="h-3.5 w-3.5" />
              ) : (
                <Mic className="h-4 w-4" />
              )}
            </button>

            <div className="flex-1 min-w-0 flex items-end bg-theme-surface rounded-3xl border border-theme/20 pl-3 pr-1 py-0.5 transition-colors">
              <textarea
                ref={textareaRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKey}
                placeholder={recorder.recording ? 'Recording voice note…' : editingMessage ? 'Edit message' : 'Message'}
                rows={1}
                disabled={recorder.recording}
                className="flex-1 min-w-0 resize-none max-h-24 bg-transparent border-0 text-theme-text text-xs placeholder:text-theme-muted py-1.5 px-0 !outline-none !ring-0 !shadow-none focus:!outline-none focus:!ring-0 focus:!shadow-none disabled:opacity-60"
                style={{ outline: 'none', boxShadow: 'none' }}
                data-testid={`chat-window-input-${conversationId}`}
              />

              <button
                type="button"
                onClick={() => {
                  textareaRef.current?.blur();
                  setComposerActionsOpen(false);
                  setEmojiOpen((v) => !v);
                }}
                disabled={recorder.recording}
                className="h-7 w-7 rounded-full flex items-center justify-center text-[#002FA7] hover:bg-[#002FA7]/10 active:bg-[#002FA7]/20 shrink-0 disabled:opacity-40"
                title="Add emoji"
                data-testid={`chat-window-emoji-${conversationId}`}
                aria-label="Add emoji"
              >
                <Smile className="h-4 w-4" />
              </button>
            </div>

            <Button
              onClick={send}
              disabled={sending || uploading || recorder.recording || !draft.trim()}
              size="sm"
              className="bg-[#002FA7] hover:bg-[#0055FF] text-white h-8 w-8 p-0 shrink-0 rounded-full"
              data-testid={`chat-window-send-${conversationId}`}
              aria-label={editingMessage ? 'Save edited message' : 'Send message'}
            >
              <Send className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {plannerOpen && (
        <PlannerQuickAddModal
          open={plannerOpen}
          onClose={() => setPlannerOpen(false)}
          context={{
            source: 'messenger',
            conversation_id: conversationId,
            other_user_id: directOther?.user_id || null,
            other_user_name: otherName,
          }}
        />
      )}

      <ConnectionProfileDrawer
        open={!!profileDrawerUserId}
        onOpenChange={(open) => {
          if (!open) setProfileDrawerUserId(null);
        }}
        userId={profileDrawerUserId}
      />

      {recordVideoOpen && (
        <RecordVideoModal
          open={recordVideoOpen}
          onClose={() => setRecordVideoOpen(false)}
          onSend={async (file) => {
            setUploading(true);
            try {
              const attachment = await uploadOne(file);
              await sendWithAttachments({
                text: '',
                attachments: [attachment],
                message_type: 'video',
              });
              setRecordVideoOpen(false);
            } catch (error) {
              toast.error(error?.response?.data?.detail || 'Could not upload the recorded video.');
            } finally {
              setUploading(false);
            }
          }}
        />
      )}

      {callsApi?.call?.conversationId === conversationId && (
        <CallOverlay
          call={callsApi.call}
          muted={callsApi.muted}
          cameraOff={callsApi.cameraOff}
          localStream={callsApi.localStream}
          remoteStream={callsApi.remoteStream}
          acceptCall={callsApi.acceptCall}
          declineCall={callsApi.declineCall}
          endCall={callsApi.endCall}
          toggleMute={callsApi.toggleMute}
          toggleCamera={callsApi.toggleCamera}
        />
      )}

      {lightboxUrl && (
        <Dialog open onOpenChange={() => setLightboxUrl(null)}>
          <DialogContent className="max-w-4xl w-[95vw] p-0 bg-black border-black overflow-hidden">
            <DialogHeader className="sr-only">
              <DialogTitle>Image preview</DialogTitle>
              <DialogDescription>Enlarged view of a shared image.</DialogDescription>
            </DialogHeader>
            <img
              src={lightboxUrl}
              alt=""
              className="w-full h-auto max-h-[85vh] object-contain"
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
